import { Card, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, ChevronDown, ChevronUp } from "lucide-react";
import Map from "@/components/Map";
import { Tool } from "@/types/tool";
import CategoryFilter from "./CategoryFilter";
import mapboxgl from 'mapbox-gl';

interface MapSectionProps {
  isMapExpanded: boolean;
  onMapExpandToggle: () => void;
  filteredTools: Tool[];
  isEditing: boolean;
  latitude: number | null;
  longitude: number | null;
  onLocationSelect?: (location: { lat: number; lng: number; formattedAddress: string }) => void;
  selectedCategory: string | null;
  onSelectCategory: (category: string | null) => void;
  mapRef?: React.MutableRefObject<mapboxgl.Map | null>;
  isMobile?: boolean;
}

const MapSection = ({
  isMapExpanded,
  onMapExpandToggle,
  filteredTools,
  isEditing,
  latitude,
  longitude,
  onLocationSelect,
  selectedCategory,
  onSelectCategory,
  mapRef,
  isMobile,
}: MapSectionProps) => {
  return (
    <Card className="overflow-hidden bg-white/90 backdrop-blur-sm shadow-xl border-0 transition-all duration-500 hover:shadow-2xl w-full">
      <CardHeader className="flex flex-row items-center justify-between py-3 sm:py-5 px-4 sm:px-6 bg-gradient-to-r from-purple-50/80 via-white to-purple-50/80 border-b border-purple-100/50">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="p-2 sm:p-2.5 bg-purple-100/50 rounded-xl">
            <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
          </div>
          <h2 className="text-lg sm:text-2xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-purple-900">
            Location Map
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onMapExpandToggle}
          className="sm:hidden hover:bg-purple-50 text-purple-700"
        >
          {isMapExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </CardHeader>
      <div className="relative">
        <div 
          className={`transition-all duration-500 ease-in-out ${
            isMobile 
              ? isMapExpanded ? 'h-[80vh]' : 'h-[45vh]'
              : isMapExpanded ? 'h-[80vh]' : 'h-[250px] sm:h-[400px] md:h-[500px] lg:h-[600px]'
          }`}
        >
          <div className="absolute inset-0 overflow-hidden">
            <Map
              tools={filteredTools}
              onLocationSelect={onLocationSelect}
              selectedLocation={latitude && longitude ? { lat: latitude, lng: longitude } : undefined}
              isSelectingLocation={isEditing}
              mapRef={mapRef}
              isMobile={isMobile}
            />
          </div>
        </div>
        <div className="p-3 sm:p-6 overflow-x-auto no-scrollbar bg-gradient-to-b from-white/80 to-transparent">
          <CategoryFilter 
            selectedCategory={selectedCategory}
            onSelectCategory={onSelectCategory}
          />
        </div>
      </div>
    </Card>
  );
};

export default MapSection;